/**
 * The LV surface queue-query client — ONE implementation in the harness,
 * consumed by every queue-native surface as `../../shell/queueQuery`
 * (the surface contract allows shell imports only). Pair it with the
 * surface's own runtime instance:
 *
 *   // src/surfaces/<id>/runtime.ts (synced from the surface repo)
 *   import { createSurfaceRuntime } from '../../shell/surfaceRuntime'
 *   import { createQueueQuery } from '../../shell/queueQuery'
 *   export const runtime = createSurfaceRuntime('<id>')
 *   export const { query, fetchBlobUrl } = createQueueQuery('<id>', runtime)
 *
 * Flow (the queue-native read contract, plan §4):
 *   POST /q {operation, params} → 202 {jobId} → GET /q/:jobId/stream (SSE via
 *   apiFetch — plain fetch CAN carry the shell-session bearer, which is why
 *   EventSource was off the table) → ONE terminal `data:` frame. The BFF
 *   answers a job that finished before the stream connected with an immediate
 *   plain-JSON snapshot on the same route (the completed-before-connect race)
 *   — the client treats it exactly like a terminal frame. When the stream
 *   cannot deliver (network error, non-OK response, ended early, or the
 *   legacy-#token path whose bearer only the runtime carries) the client
 *   falls back to polling GET /q/:jobId every ~1s for the REMAINING deadline.
 *
 * Dedupe + prefetch: identical (surfaceId, operation, params) queries share
 * ONE promise — in-flight, or settled within the last 10s. StrictMode
 * double-fires collapse into one POST; prefetchSurfaceQuery (hover/focus on a
 * catalogue card) warms the map so the real click joins the in-flight or
 * freshly-settled promise instead of starting cold.
 *
 * Terminal handling mirrors the BFF reply pump:
 *   done + {kind:'json'}  → resolves the body
 *   done + {kind:'blob'}  → resolves a QueueBlobRef (check with isBlobRef);
 *                           bytes come from fetchBlobUrl (bearer-attached
 *                           fetch → object URL — an <img>/<a> cannot carry
 *                           the bearer either)
 *   done + {kind:'error'} → throws Error(message) with .status (the worker's
 *                           expected, client-visible failures — bad params,
 *                           gated off, not found)
 *   failed/poison         → throws Error(snapshot.error || 'query failed')
 *   timeout frame / >75s  → throws 'the request timed out — try again'
 *                           (the BFF SSE timeout, same wording)
 */
import { apiFetch } from './auth'
import { createSurfaceRuntime, type SurfaceRuntime } from './surfaceRuntime'

export interface QueueBlobRef {
  kind: 'blob'
  blobId: string
  contentType: string
  filename: string | null
}

export function isBlobRef(v: unknown): v is QueueBlobRef {
  return !!v && typeof v === 'object' && (v as QueueBlobRef).kind === 'blob' &&
    typeof (v as QueueBlobRef).blobId === 'string'
}

export interface QueueQueryClient {
  /** Run a data-plane operation and resolve its JSON body (or a QueueBlobRef
   *  for blob-producing ops — check isBlobRef when the op can return one). */
  query: <T = unknown>(operation: string, params?: Record<string, unknown>) => Promise<T>
  /** Claim-check bytes as a revocable object URL (revoke() after use). */
  fetchBlobUrl: (blobId: string) => Promise<{ url: string; contentType: string; revoke: () => void }>
}

interface Snapshot {
  status: 'pending' | 'in_progress' | 'done' | 'failed' | 'poison' | 'timeout'
  result: Record<string, unknown> | null
  error: string | null
}

const POLL_MS = 1_000
const DEADLINE_MS = 75_000 // the BFF SSE timeout (envelope ttl 60s + grace)
const RETAIN_MS = 10_000 // settled-result retention for the dedupe map

const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms))

/** setTimeout that never holds a Node process open (vitest/ssr); a no-op in
 *  the browser where the timer is a number. */
function later(ms: number, fn: () => void): ReturnType<typeof setTimeout> {
  const t = setTimeout(fn, ms)
  ;(t as unknown as { unref?: () => void }).unref?.()
  return t
}

// ── in-flight dedupe + settled-result retention ─────────────────────────────

/** stableStringify: JSON with recursively-sorted object keys, so
 *  {a:1,b:2} and {b:2,a:1} dedupe onto one job. */
function stableStringify(value: unknown): string {
  if (value === null || typeof value !== 'object') return JSON.stringify(value) ?? 'undefined'
  if (Array.isArray(value)) return `[${value.map(stableStringify).join(',')}]`
  const entries = Object.keys(value as Record<string, unknown>)
    .sort()
    .map((k) => `${JSON.stringify(k)}:${stableStringify((value as Record<string, unknown>)[k])}`)
  return `{${entries.join(',')}}`
}

const shared = new Map<string, Promise<unknown>>()

// ── SSE frame parsing (pure — unit-tested) ──────────────────────────────────

/** Incremental SSE frame splitter: push decoded text, get back each COMPLETE
 *  frame's data payload. Comment/keepalive frames (`:ok`) and frames with no
 *  data: lines yield nothing; a trailing partial frame stays buffered for the
 *  next push. Mirrors the BFF's exact frame format — `data: <json>\n\n`. */
export function createSseFrameSplitter(): { push: (chunk: string) => string[] } {
  let buf = ''
  return {
    push(chunk: string): string[] {
      buf += chunk
      const out: string[] = []
      let idx: number
      while ((idx = buf.indexOf('\n\n')) >= 0) {
        const frame = buf.slice(0, idx)
        buf = buf.slice(idx + 2)
        const data = frame
          .split('\n')
          .filter((l) => l.startsWith('data:'))
          .map((l) => l.slice(5).replace(/^ /, ''))
          .join('\n')
        if (data) out.push(data)
      }
      return out
    },
  }
}

/** Terminal handling shared by the SSE frame, the JSON race-snapshot, and the
 *  poll fallback: resolves for done, throws for every failure kind. Returns
 *  terminal=false only for pending/in_progress. */
function settleFromSnapshot<T>(snap: Snapshot): { terminal: boolean; value?: T } {
  if (snap.status === 'pending' || snap.status === 'in_progress') return { terminal: false }
  if (snap.status === 'failed' || snap.status === 'poison') {
    throw new Error(snap.error || 'the query failed — try again')
  }
  if (snap.status === 'timeout') {
    throw new Error(snap.error || 'the request timed out — try again')
  }
  const result = snap.result as { kind?: string; body?: unknown; error?: { status: number; message: string } } | null
  if (result?.kind === 'error') {
    const err = new Error(result.error?.message ?? 'the query failed') as Error & { status?: number }
    err.status = result.error?.status
    throw err
  }
  if (result?.kind === 'blob') return { terminal: true, value: result as unknown as T }
  return { terminal: true, value: (result?.body ?? null) as T }
}

export function createQueueQuery(
  surfaceId: string,
  rt: Pick<SurfaceRuntime, 'api' | 'apiSend'>,
): QueueQueryClient {
  /** SSE-first wait. Resolves on a terminal frame/snapshot; returns undefined
   *  when the stream itself couldn't deliver one (the caller then polls for
   *  the remaining deadline). Terminal ERRORS throw — never fall back on a
   *  real answer. Aborts at the shared deadline so a silently-buffered proxy
   *  can't hang the query past 75s. */
  async function waitViaStream<T>(jobId: string, deadline: number): Promise<{ value: T } | undefined> {
    const ac = new AbortController()
    const timer = later(Math.max(0, deadline - Date.now()), () => ac.abort())
    let terminal: Snapshot | null = null
    try {
      const res = await apiFetch(`/LearnerView/api/surfaces/${surfaceId}/q/${jobId}/stream`, { signal: ac.signal })
      if (!res.ok || !res.body) return undefined // 401/503/etc → the poll path reports the server error
      const ct = res.headers.get('content-type') ?? ''
      if (!ct.includes('text/event-stream')) {
        // Completed-before-connect: the BFF answers the race with a plain
        // JSON snapshot instead of a stream. Same terminal handling below.
        try { terminal = (await res.json()) as Snapshot } catch { terminal = null }
      } else {
        const split = createSseFrameSplitter()
        const reader = res.body.getReader()
        const dec = new TextDecoder()
        try {
          for (;;) {
            const { done, value } = await reader.read()
            if (done) break
            for (const payload of split.push(dec.decode(value, { stream: true }))) {
              let snap: Snapshot | null = null
              try { snap = JSON.parse(payload) as Snapshot } catch { /* not a snapshot frame */ }
              if (snap && snap.status !== 'pending' && snap.status !== 'in_progress') {
                terminal = snap
                break
              }
            }
            if (terminal) break
          }
        } finally {
          try { void reader.cancel().catch(() => undefined) } catch { /* already closed */ }
        }
      }
    } catch {
      return undefined // network/read failure (incl. the deadline abort) → poll
    } finally {
      clearTimeout(timer)
    }
    if (!terminal) return undefined // ended early with no answer → poll
    // Outside every catch: a terminal settle THROWS the query's real errors
    // (failed/poison/timeout/error-kind) straight to the caller — a real
    // answer must never trigger the poll fallback.
    const s = settleFromSnapshot<T>(terminal)
    return s.terminal ? { value: s.value as T } : undefined
  }

  async function runQuery<T = unknown>(operation: string, params: Record<string, unknown>): Promise<T> {
    const accepted = await rt.apiSend<{ jobId: string }>('/q', 'POST', { operation, params })
    const deadline = Date.now() + DEADLINE_MS
    const streamed = await waitViaStream<T>(accepted.jobId, deadline)
    if (streamed) return streamed.value
    // Stream couldn't deliver — poll for the REMAINING deadline.
    for (;;) {
      await sleep(POLL_MS)
      const snap = await rt.api<Snapshot>(`/q/${accepted.jobId}`)
      const s = settleFromSnapshot<T>(snap)
      if (s.terminal) return s.value as T
      if (Date.now() > deadline) throw new Error('the request timed out — try again')
    }
  }

  function query<T = unknown>(operation: string, params?: Record<string, unknown>): Promise<T> {
    const key = `${surfaceId}:${operation}:${stableStringify(params ?? {})}`
    const existing = shared.get(key)
    if (existing) return existing as Promise<T>
    const promise = runQuery<T>(operation, params ?? {})
    shared.set(key, promise)
    // Retention starts at SETTLE: a hover prefetch's promise is still warm
    // when the click lands moments later; failures clear on the same clock.
    promise.then(
      () => later(RETAIN_MS, () => shared.delete(key)),
      () => later(RETAIN_MS, () => shared.delete(key)),
    )
    return promise
  }

  async function fetchBlobUrl(blobId: string): Promise<{ url: string; contentType: string; revoke: () => void }> {
    // apiFetch attaches the shell-session bearer; a bare <img>/<a> could not.
    const r = await apiFetch(`/LearnerView/api/surfaces/${surfaceId}/blob/${blobId}`)
    if (!r.ok) {
      let msg = `${r.status}`
      try { msg = ((await r.json()) as { error?: string }).error ?? msg } catch { /* keep status */ }
      throw new Error(msg)
    }
    const contentType = r.headers.get('content-type') ?? 'application/octet-stream'
    const blob = await r.blob()
    const url = URL.createObjectURL(blob)
    return { url, contentType, revoke: () => URL.revokeObjectURL(url) }
  }

  return { query, fetchBlobUrl }
}

// ── hover/focus prefetch ─────────────────────────────────────────────────────

const prefetchClients = new Map<string, QueueQueryClient>()

/** Warm the dedupe map for a query the learner is LIKELY to run (a catalogue
 *  card's hover/focus → its details click). Fire-and-forget: failures are
 *  swallowed here — the real click reports its own error. createSurfaceRuntime
 *  is side-effect-free at construction (token adoption only happens in
 *  adoptEntryParams, which we never call), so a memoized minimal runtime per
 *  surface is safe for surfaces the shell hasn't resolved. */
export function prefetchSurfaceQuery(surfaceId: string, operation: string, params: Record<string, unknown>): void {
  try {
    let client = prefetchClients.get(surfaceId)
    if (!client) prefetchClients.set(surfaceId, (client = createQueueQuery(surfaceId, createSurfaceRuntime(surfaceId))))
    void client.query(operation, params).catch(() => undefined)
  } catch { /* prefetch is a nicety — never break the caller */ }
}
